/**
 * @element qds-gradient-line
 * 
 * A horizontal gradient line component that extends across the viewport.
 * 
 * @attr {string} start-color - The starting color of the gradient (default: var(--theme-success))
 * @attr {string} end-color - The ending color of the gradient (default: var(--theme-accent-primary))
 * @attr {number} height - The height of the line in pixels (default: 3)
 * 
 * @csspart line - The horizontal line element
 */

import { loadBaseCSS } from '../shared/button-utils.js';

// Load base CSS
loadBaseCSS();

const DEFAULT_START_COLOR = 'var(--theme-success, var(--brand-green))';
const DEFAULT_END_COLOR = 'var(--theme-accent-primary, var(--brand-middle-blue))';

export class QdsGradientLine extends HTMLElement {
  // Private properties
  private _startColor: string;
  private _endColor: string;
  private _height: number;

  static get observedAttributes(): string[] {
    return ['start-color', 'end-color', 'height'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Initialize default properties
    this._startColor = DEFAULT_START_COLOR;
    this._endColor = DEFAULT_END_COLOR;
    this._height = 3;

    this.render();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    // No additional setup needed for now
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'start-color':
        this._startColor = newValue || DEFAULT_START_COLOR;
        break;
      case 'end-color':
        this._endColor = newValue || DEFAULT_END_COLOR;
        break;
      case 'height': {
        const height = parseInt(newValue, 10);
        this._height = isNaN(height) ? 3 : height;
        break;
      }
    }

    this.render();
  }

  // Getters and setters
  get startColor(): string {
    return this._startColor;
  }

  set startColor(value: string) {
    this.setAttribute('start-color', value);
  }

  get endColor(): string {
    return this._endColor;
  }

  set endColor(value: string) {
    this.setAttribute('end-color', value);
  }

  get height(): number {
    return this._height;
  }

  set height(value: number) {
    this.setAttribute('height', value.toString());
  }

  private render(): void {
    if (!this.shadowRoot) return;
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        hr {
          width: 100%;
          height: ${this._height}px;
          background: linear-gradient(108deg, ${this._startColor} 37.86%, ${this._endColor} 80.26%);
          background-blend-mode: darken;
          margin: 0;
          border: 0;
          position: relative;
          z-index: 99;
          box-sizing: border-box;
        }
      </style>
      
      <hr part="line">
    `;
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-gradient-line')) {
  customElements.define('qds-gradient-line', QdsGradientLine);
}
